/**
 * Custom groupings for a flashcard class — JK/SK, reading groups, or whatever
 * a teacher actually splits their room by.
 *
 * A kinder class is usually two cohorts in one room, and almost every task in
 * it is "all my JKs, then all my SKs": running the deck, reading the class
 * chart, filing results against a report. An ESL class splits the same way by
 * proficiency or arrival year. The roster order the grid used before this
 * served none of that.
 *
 * ONE label per child, up to four per class. Not a free-for-all of tags,
 * because the whole point is a single unambiguous grouping to sort by — a child
 * in two groups has no place in an ordered list. The labels are ordered, and
 * that order IS the sort order: a teacher who wants JK before SK writes them in
 * that order rather than hunting for a sort direction.
 *
 * Students carry the label's ID, never its text, so renaming "JK" to "Junior"
 * does not orphan every child who had it.
 */

import { byStudentName } from './studentName';
import type { KinderLabel, KinderYear } from '../types';

export type { KinderLabel, KinderYear };

/**
 * The two kindergarten cohorts, in the order a teacher lists them.
 *
 * Separate from the custom labels on purpose: this split is needed on nearly
 * every task in the room, so it gets its own one-click control and its own sort,
 * and the four labels stay free for whatever else the class divides by.
 */
export const KINDER_YEARS: readonly KinderYear[] = ['JK', 'SK'] as const;

/** Read a stored year, rejecting anything that is not one of the two. */
export const kinderYearOf = (student: { kinderYear?: unknown } | null | undefined): KinderYear | '' => {
  const v = typeof student?.kinderYear === 'string' ? student.kinderYear.trim().toUpperCase() : '';
  return (KINDER_YEARS as readonly string[]).includes(v) ? (v as KinderYear) : '';
};

/** Four is the cap. Beyond that the grouping stops being a grouping. */
export const MAX_KINDER_LABELS = 4;

/** How the kinder student lists are ordered. */
export type KinderSortMode = 'roster' | 'name' | 'label' | 'year';

interface LabelledStudent {
  firstName?: string;
  lastName?: string;
  kinderLabelId?: string;
  kinderYear?: KinderYear;
}

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * Clean a stored label list into something safe to render and sort by.
 *
 * Applied on READ as well as write: a class doc can hold anything an older
 * build or a half-finished edit left behind, and a blank or duplicated label
 * makes the grouping meaningless rather than merely untidy.
 */
export function normalizeKinderLabels(raw: unknown): KinderLabel[] {
  if (!Array.isArray(raw)) return [];
  const out: KinderLabel[] = [];
  const seenNames = new Set<string>();
  const seenIds = new Set<string>();
  for (const entry of raw) {
    const id = text((entry as KinderLabel)?.id);
    const name = text((entry as KinderLabel)?.name);
    if (!id || !name) continue;
    if (seenIds.has(id)) continue;
    // "JK" and "jk" are one group a teacher typed twice, not two groups.
    const key = name.toLowerCase();
    if (seenNames.has(key)) continue;
    seenIds.add(id);
    seenNames.add(key);
    out.push({ id, name });
    if (out.length >= MAX_KINDER_LABELS) break;
  }
  return out;
}

/** The label a student carries, or null — including when it points at a label that is gone. */
export function labelFor(labels: KinderLabel[], student: LabelledStudent | null | undefined): KinderLabel | null {
  const id = text(student?.kinderLabelId);
  if (!id) return null;
  return labels.find(l => l.id === id) || null;
}

/**
 * Where a student sits in the grouping.
 *
 * Unlabelled children — and children whose label was deleted — sort AFTER
 * every named group rather than silently ahead of it. A teacher who has
 * labelled nine of ten needs the tenth to stand out, not to lead.
 */
export function labelRank(labels: KinderLabel[], student: LabelledStudent): number {
  const hit = labelFor(labels, student);
  return hit ? labels.findIndex(l => l.id === hit.id) : labels.length;
}

/**
 * Order students for every kinder surface — grid, chart, CSV, dropdown.
 *
 * 'roster' returns the array untouched, which is what the view did before
 * labels existed and remains the default: a teacher who has arranged their
 * roster deliberately should not have it silently re-sorted.
 */
export function orderStudentsForKinder<T extends LabelledStudent>(
  students: T[] | null | undefined,
  labels: KinderLabel[],
  mode: KinderSortMode,
): T[] {
  const list = Array.isArray(students) ? students : [];
  if (mode === 'roster') return list;
  const sorted = [...list];
  if (mode === 'name') return sorted.sort(byStudentName);
  if (mode === 'year') {
    // JK block, then SK, then anyone not yet tagged — who go LAST rather than
    // first, so an untagged child stands out as needing a tag instead of hiding
    // at the top of the list where a teacher reads a heading.
    const rank = (s: LabelledStudent) => {
      const y = kinderYearOf(s);
      return y ? KINDER_YEARS.indexOf(y) : KINDER_YEARS.length;
    };
    return sorted.sort((a, b) => (rank(a) - rank(b)) || byStudentName(a, b));
  }
  return sorted.sort((a, b) => {
    const rank = labelRank(labels, a) - labelRank(labels, b);
    // Alphabetical INSIDE each group — the grouping decides the blocks, the
    // name decides the order within one, which is how a class list reads.
    return rank !== 0 ? rank : byStudentName(a, b);
  });
}

/**
 * Remove a label, and take it off everyone who had it.
 *
 * Returned together because doing one without the other is the bug: a student
 * left pointing at a deleted label reads as unlabelled everywhere but still
 * carries a dead id that a later label reusing that id would resurrect.
 */
export function removeKinderLabel<T extends LabelledStudent & { kinderLabelId?: string }>(
  labels: KinderLabel[], students: T[], labelId: string,
): { labels: KinderLabel[]; students: T[]; clearedCount: number } {
  let clearedCount = 0;
  const nextStudents = students.map(s => {
    if (s.kinderLabelId !== labelId) return s;
    clearedCount++;
    const next = { ...s };
    delete next.kinderLabelId;
    return next;
  });
  return { labels: labels.filter(l => l.id !== labelId), students: nextStudents, clearedCount };
}

/**
 * Add a label to a list, enforcing the cap and the no-duplicates rule.
 *
 * Pure and separate from the UI because the check that matters runs INSIDE a
 * transaction, against whatever the class doc holds now — not against whatever
 * the teacher's screen last rendered. Two quick additions from one screen were
 * both built on the same stale list, and the second silently dropped the first.
 */
export function appendKinderLabel(
  labels: KinderLabel[], name: string, id: string,
): { labels: KinderLabel[]; ok: boolean; reason?: 'cap' | 'duplicate' | 'blank' } {
  const clean = text(name);
  if (!clean) return { labels, ok: false, reason: 'blank' };
  if (labels.length >= MAX_KINDER_LABELS) return { labels, ok: false, reason: 'cap' };
  if (labels.some(l => l.name.toLowerCase() === clean.toLowerCase())) {
    return { labels, ok: false, reason: 'duplicate' };
  }
  return { labels: [...labels, { id, name: clean }], ok: true };
}

/**
 * Put one child in a label, or take them out of every label.
 *
 * Returns the SAME array when nothing changes, so a caller can skip a write
 * rather than send one that only rewrites what is already there.
 */
export function applyStudentKinderLabel<T extends { id: string; kinderLabelId?: string }>(
  students: T[], studentId: string, labelId: string,
): T[] {
  const target = students.find(s => s.id === studentId);
  if (!target) return students;
  const next = text(labelId);
  if ((target.kinderLabelId || '') === next) return students;
  return students.map(s => {
    if (s.id !== studentId) return s;
    const copy = { ...s };
    // Delete rather than set undefined: Firestore rejects undefined, and a
    // key left behind would read as "still labelled" to `in` checks.
    if (next) copy.kinderLabelId = next; else delete copy.kinderLabelId;
    return copy;
  });
}

/**
 * Set or clear one child's JK/SK.
 *
 * Returns the SAME array when nothing changes, so the caller can skip a write.
 * Passing the year the child already has CLEARS it, because the control is two
 * buttons and tapping the lit one is how anyone expects to undo a toggle.
 */
export function applyStudentKinderYear<T extends { id: string; kinderYear?: KinderYear }>(
  students: T[], studentId: string, year: KinderYear | '',
): T[] {
  const target = students.find(s => s.id === studentId);
  if (!target) return students;
  /**
   * '' clears. Anything else must be a real year or this does NOTHING.
   *
   * It used to fold every unrecognised value into "clear", so a caller passing
   * 'sk' or 'Senior' would silently wipe the child's cohort instead of setting it
   * or being refused. No caller does that today — YearToggle passes the constants
   * — but an import or a server writer would, and a silent wipe is the one outcome
   * nobody would notice.
   */
  const valid = (KINDER_YEARS as readonly string[]).includes(year);
  if (year !== '' && !valid) return students;
  const next: KinderYear | '' = valid ? (year as KinderYear) : '';
  if (kinderYearOf(target) === next) return students;
  return students.map(s => {
    if (s.id !== studentId) return s;
    const copy = { ...s };
    // Delete rather than set undefined: Firestore rejects undefined, and a key
    // left behind reads as "still tagged" to an `in` check.
    if (next) copy.kinderYear = next; else delete copy.kinderYear;
    return copy;
  });
}

/**
 * Which optimistic label picks the server has caught up on.
 *
 * The screen shows a pick immediately — a dropdown that snaps back to the old
 * value for a round trip reads as a broken control on school Wi-Fi — so the
 * pending pick is overlaid until the class doc agrees, then dropped. Comparing
 * against the doc rather than clearing on write-success is what makes a
 * clobbered or rejected write show the truth instead of the hope.
 */
export function settledPendingLabels(
  students: { id: string; kinderLabelId?: string }[],
  pending: Record<string, string>,
): string[] {
  return Object.keys(pending).filter(sid => {
    const st = students.find(s => s.id === sid);
    if (!st) return true; // student is gone; the pick has nowhere to land
    return (st.kinderLabelId || '') === (pending[sid] || '');
  });
}

/**
 * Which optimistic JK/SK picks the server has caught up on.
 *
 * Same contract as settledPendingLabels, and for the same reason: compare against
 * the STORED value rather than clearing on write-success, so a clobbered or
 * rejected write shows the truth instead of the hope.
 */
export function settledPendingYears(
  students: { id: string; kinderYear?: KinderYear }[],
  pending: Record<string, KinderYear | ''>,
): string[] {
  return Object.keys(pending).filter(sid => {
    const st = students.find(s => s.id === sid);
    if (!st) return true; // student is gone; the pick has nowhere to land
    return kinderYearOf(st) === (pending[sid] || '');
  });
}
